import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Modal, Button, Form, Icon, Label } from 'semantic-ui-react'
import PropTypes from "prop-types";
import { RoomPlannedMaintenance } from '../../api/services/next';
import { onSuccess, onError, onWarning } from '../shared/messages'
import { useTextResources, useMessageResources } from '../../language';
import { DatePicker } from '../shared/DatePicker';
import moment from 'moment';
import { useLoader } from '../../hooks/useLoader';
import RoomsDropDown from '../shared/RoomsDropDown';
import HotelsDropDown from '../shared/HotelsDropDown';
import { isMobileOnly } from 'mobile-device-detect';
import ValidateModalPermission from '../shared/ValidateModalPermission';
import { PermissionsActions, UserHasPermission } from '../shared/permissionsActions';
import { toServerDateFormat, handleStartEndChange } from '../shared/utils';
import { useConfirm } from '../../hooks/useConfirm';
import { dateFormat } from '../shared/constants';
import WeekDaysSelect from '../shared/WeekDaysSelect';
import RadioSelectionInput from '../shared/RadioSelectionInput';
import './style.less';


const RoomMaintenancePopup = ({ onClose, maintenance: initialMaintenance, onSave, ...rest }) => {
    const text = useTextResources()
    const message = useMessageResources()
    const loader = useLoader()
    const confirm = useConfirm()

    // Core state
    const [maintenance, setMaintenance] = useState({})
    const [originalMaintenanceTypeId, setOriginalMaintenanceTypeId] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [businessDate, setBusinessDate] = useState(new Date())

    // Multiple date ranges (create mode only)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [selectedDates, setSelectedDates] = useState([])

    // Weekday selection
    const [selectedDays, setSelectedDays] = useState([])

    // Multiple rooms (create mode only)
    const [selectedRoomIds, setSelectedRoomIds] = useState([])

    // Guard to skip isDirty on initial WeekDaysSelect mount/settle callbacks
    const isInitialDaysCallback = useRef(true)
    const initialDaysTimerRef = useRef(null)

    // Cleanup timer on unmount
    useEffect(() => {
        return () => clearTimeout(initialDaysTimerRef.current);
    }, [])

    useEffect(() => {
        setBusinessDate(new Date())
    }, [])

    useEffect(() => {
        if (rest.open) {
            if (initialMaintenance) {
                const isEdit = initialMaintenance.Id > 0 || !!initialMaintenance.IsVirtual;
                const typeId = initialMaintenance.MaintenanceStatusTypeId;

                // Permission gate: block opening if user lacks required permission
                if (isEdit) {
                    // Edit mode: need edit permission for the maintenance's current type
                    if (typeId && !canEdit(typeId)) {
                        onWarning(text("NoPermission"));
                        if (onClose) onClose();
                        return;
                    }
                } else {
                    // Create mode: need at least one create permission (OOO or OOS)
                    if (!UserHasPermission(PermissionsActions.CreateOOO) && !UserHasPermission(PermissionsActions.CreateOOS)) {
                        onWarning(text("NoPermission"));
                        if (onClose) onClose();
                        return;
                    }
                }

                // Auto-select maintenance type in create mode based on permissions
                // If user only has one create permission, force that type regardless of what was passed in
                const hasCreateOOS = UserHasPermission(PermissionsActions.CreateOOS);
                const hasCreateOOO = UserHasPermission(PermissionsActions.CreateOOO);
                const autoTypeId = !isEdit
                    ? (hasCreateOOS && !hasCreateOOO ? 1
                        : !hasCreateOOS && hasCreateOOO ? 2
                        : null)
                    : null;

                setMaintenance({
                    ...initialMaintenance,
                    StartDate: moment(initialMaintenance.StartDate).toDate(),
                    EndDate: moment(initialMaintenance.EndDate).toDate(),
                    ...(autoTypeId ? { MaintenanceStatusTypeId: autoTypeId } : {})
                })
                // Store original maintenance type for permission checks in edit mode
                setOriginalMaintenanceTypeId(initialMaintenance.MaintenanceStatusTypeId || null)
                // Edit mode: single room
                setSelectedRoomIds(initialMaintenance.RoomId ? [initialMaintenance.RoomId] : [])
                setSelectedDays([])
                // Create mode (Id === 0): pre-populate dates from calendar selection
                if (!initialMaintenance.Id && initialMaintenance.StartDate && initialMaintenance.EndDate) {
                    const initStart = moment(initialMaintenance.StartDate).toDate();
                    const initEnd = moment(initialMaintenance.EndDate).toDate();
                    setStartDate(initStart)
                    setEndDate(initEnd)
                    setSelectedDates([{ startDate: initStart, endDate: initEnd }])
                } else {
                    setSelectedDates([])
                    setStartDate(null)
                    setEndDate(null)
                }
            } else {
                // No maintenance object — create mode, check at least one create permission
                if (!UserHasPermission(PermissionsActions.CreateOOO) && !UserHasPermission(PermissionsActions.CreateOOS)) {
                    onWarning(text("NoPermission"));
                    if (onClose) onClose();
                    return;
                }
                // Auto-select maintenance type based on permissions when only one is available
                const autoTypeId = UserHasPermission(PermissionsActions.CreateOOS) && !UserHasPermission(PermissionsActions.CreateOOO) ? 1
                    : !UserHasPermission(PermissionsActions.CreateOOS) && UserHasPermission(PermissionsActions.CreateOOO) ? 2
                    : null;
                setMaintenance(autoTypeId ? { MaintenanceStatusTypeId: autoTypeId } : {})
                setSelectedRoomIds([])
                setSelectedDates([])
                setSelectedDays([])
                setStartDate(null)
                setEndDate(null)
            }
            setIsDirty(false)
            isInitialDaysCallback.current = true
        }
    }, [rest.open, initialMaintenance])

    const isEditMode = maintenance.Id > 0 || !!maintenance.IsVirtual;
    const isVirtual = !!maintenance.IsVirtual;

    // Permission checks
    const canCreate = useCallback((typeId) => {
        if (typeId === 2) return UserHasPermission(PermissionsActions.CreateOOO);
        if (typeId === 1) return UserHasPermission(PermissionsActions.CreateOOS);
        return false;
    }, []);

    const canEdit = useCallback((typeId) => {
        if (typeId === 2) return UserHasPermission(PermissionsActions.EditOOO);
        if (typeId === 1) return UserHasPermission(PermissionsActions.EditOOS);
        return false;
    }, []);

    const canDelete = useCallback((typeId) => {
        if (typeId === 2) return UserHasPermission(PermissionsActions.DeleteOOO);
        if (typeId === 1) return UserHasPermission(PermissionsActions.DeleteOOS);
        return false;
    }, []);

    // Validate minimum 1 night
    const isMinimumOneNight = useCallback((start, end) => {
        if (!start || !end) return true;
        const s = moment(start).startOf('day');
        const e = moment(end).startOf('day');
        return e.diff(s, 'days') >= 1;
    }, []);

    // Check if a new range overlaps with any existing range
    // Two ranges overlap if newStart < existingEnd AND newEnd > existingStart
    // Touching boundaries (e.g. newStart == existingEnd) are allowed and not considered overlap
    const doesRangeOverlapExistingRanges = useCallback((newStart, newEnd, existingRanges) => {
        if (!newStart || !newEnd || !existingRanges || existingRanges.length === 0) return false;
        const newStartDay = moment(newStart).startOf('day');
        const newEndDay = moment(newEnd).startOf('day');
        return existingRanges.some(range => {
            const rangeStart = moment(range.startDate).startOf('day');
            const rangeEnd = moment(range.endDate).startOf('day');
            // Standard interval overlap: ranges overlap if one starts before the other ends
            return newStartDay.isBefore(rangeEnd) && newEndDay.isAfter(rangeStart);
        });
    }, []);

    // Memo for dates to pass to WeekDaysSelect - combines all date ranges
    const memoDatesForWeekdays = useMemo(() => {
        if (isEditMode) {
            return maintenance.StartDate && maintenance.EndDate
                ? [{ startDate: maintenance.StartDate, endDate: maintenance.EndDate }]
                : [];
        }
        const effective = selectedDates.length > 0 ? selectedDates : (startDate && endDate ? [{ startDate, endDate }] : []);
        return effective;
    }, [isEditMode, maintenance.StartDate, maintenance.EndDate, selectedDates, startDate, endDate]);

    // Weekday change handler — skip isDirty on initial mount/settle callbacks from WeekDaysSelect.
    // WeekDaysSelect fires onChange multiple times during initial render (mount + dates prop update),
    // so we keep the guard active and only clear it after a short delay to absorb all initial callbacks.
    const handleDaysChange = useCallback((days) => {
        setSelectedDays(days);
        if (isInitialDaysCallback.current) {
            // Reset the timer on each initial callback so the guard stays active
            // until WeekDaysSelect has fully settled
            clearTimeout(initialDaysTimerRef.current);
            initialDaysTimerRef.current = setTimeout(() => {
                isInitialDaysCallback.current = false;
            }, 100);
            return;
        }
        setIsDirty(true);
    }, []);

    // Add date range to list (create mode only)
    const addDateToList = useCallback(() => {
        if (!startDate || !endDate) {
            onWarning(text("MissingFields").replace('{0}', `${text("StartDate")}, ${text("EndDate")}`));
            return;
        }
        if (!isMinimumOneNight(startDate, endDate)) {
            onWarning(message("MaintenanceMustBeAtLeastOneNight"));
            return;
        }
        if (selectedDates.some(d => moment(d.startDate).isSame(startDate, 'day') && moment(d.endDate).isSame(endDate, 'day'))) {
            onWarning(text('WarningDuplicateDates'));
            return;
        }
        // Prevent intersecting ranges: neither start nor end date must fall strictly inside an existing range
        if (doesRangeOverlapExistingRanges(startDate, endDate, selectedDates)) {
            onWarning(message("ErrorOverlappingDates"));
            return;
        }
        setSelectedDates(prev => [...prev, { startDate, endDate }]);
        setIsDirty(true);
    }, [startDate, endDate, selectedDates, isMinimumOneNight, doesRangeOverlapExistingRanges, text, message]);

    const removeDateFromList = useCallback((index) => {
        setSelectedDates(prev => prev.filter((_, i) => i !== index));
        setIsDirty(true);
    }, []);

    // Build effective date ranges: use selectedDates, or fall back to current From/To if valid
    const getEffectiveDateRanges = useCallback(() => {
        if (selectedDates.length > 0) {
            return selectedDates;
        }
        // No ranges added — use current From/To as a single range if both are filled
        // This is a guard for when user selects a range but doesn't click Add
        if (startDate && endDate && isMinimumOneNight(startDate, endDate)) {
            return [{ startDate, endDate }];
        }
        return [];
    }, [selectedDates, startDate, endDate, isMinimumOneNight]);

    // Get missing required fields
    const getMissingFields = useCallback(() => {
        const missingFields = [];
        if (!isEditMode) {
            const effectiveRanges = getEffectiveDateRanges();
            if (effectiveRanges.length === 0) missingFields.push(text("Dates"));
            if (selectedRoomIds.length === 0) missingFields.push(text("Room"));
        } else {
            if (!maintenance.StartDate) missingFields.push(text("StartDate"));
            if (!maintenance.EndDate) missingFields.push(text("EndDate"));
            if (!maintenance.RoomId) missingFields.push(text("Room"));
        }
        if (!maintenance.MaintenanceStatusTypeId) missingFields.push(text("MaintenanceType"));
        return missingFields;
    }, [isEditMode, selectedDates, selectedRoomIds, maintenance, text, getEffectiveDateRanges]);

    const saveAction = async (shouldClose = false) => {
        const typeId = maintenance.MaintenanceStatusTypeId;

        if (isEditMode) {
            // Edit mode: must have edit permission for the original type
            if (originalMaintenanceTypeId && !canEdit(originalMaintenanceTypeId)) {
                onWarning(text("NoPermission"));
                return;
            }
            // If user changed the type (e.g. OOO→OOS), they also need create permission for the NEW type
            if (typeId && originalMaintenanceTypeId && typeId !== originalMaintenanceTypeId) {
                if (!canCreate(typeId)) {
                    onWarning(text("NoPermission"));
                    return;
                }
            }
        } else {
            // Create mode: must have create permission for the selected type
            if (typeId && !canCreate(typeId)) {
                onWarning(text("NoPermission"));
                return;
            }
        }

        const missingFields = getMissingFields();
        if (missingFields.length !== 0) {
            onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
            return;
        }

        // Edit mode: validate minimum 1 night
        if (isEditMode && !isMinimumOneNight(maintenance.StartDate, maintenance.EndDate)) {
            onWarning(message("MaintenanceMustBeAtLeastOneNight"));
            return;
        }

        setIsSaving(true)
        loader(true)
        try {
            if (isEditMode) {
                // Edit mode: single record update, always closes after save
                const startDateStr = toServerDateFormat(maintenance.StartDate);
                const endDateStr = toServerDateFormat(maintenance.EndDate);

                const maintenanceToSave = {
                    ...maintenance,
                    StartDate: startDateStr,
                    EndDate: endDateStr,
                    CreateDate: maintenance.CreateDate || new Date(),
                    UpdatedByUserId: window.optima?.user?.Id,
                    IsActive: maintenance.IsActive
                };

                const apiResult = await RoomPlannedMaintenance.UpdateRoomPlannedMaintenances([maintenanceToSave]);

                // The Update API returns empty string, so we need to construct the result ourselves
                // Merge with original maintenance to preserve any server-side fields
                const updated = [{
                    ...maintenance,
                    ...maintenanceToSave,
                    StartDate: maintenance.StartDate,
                    EndDate: maintenance.EndDate
                }];

                if (onSave) onSave(updated);
                onSuccess();
                setIsDirty(false);
                if (onClose) onClose();
            } else {
                // Create mode: build records for each room × each date range
                // Use effective ranges which includes current From/To even if not explicitly added
                const effectiveRanges = getEffectiveDateRanges();
                const records = [];
                for (const roomId of selectedRoomIds) {
                    for (const dateRange of effectiveRanges) {
                        records.push({
                            Id: 0,
                            RoomId: roomId,
                            StartDate: toServerDateFormat(dateRange.startDate),
                            EndDate: toServerDateFormat(dateRange.endDate),
                            MaintenanceStatusTypeId: maintenance.MaintenanceStatusTypeId,
                            MaintenanceDays: selectedDays.length > 0 && selectedDays.length < 7 ? selectedDays : null,
                            Description: maintenance.Description,
                            HotelId: maintenance.HotelId,
                            CreateDate: new Date(),
                            UpdatedByUserId: window.optima?.user?.Id,
                            IsActive: true
                        });
                    }
                }

                const apiResult = await RoomPlannedMaintenance.AddRoomPlannedMaintenances(records);

                if (onSave) onSave(Array.isArray(apiResult) ? apiResult : records);
                onSuccess();
                setIsDirty(false);

                if (shouldClose) {
                    if (onClose) onClose();
                }
                // Save (without close): popup stays open with all current parameters kept
            }
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error);
        } finally {
            loader(false);
            setIsSaving(false);
        }
    }

    const deleteAction = async () => {
        if (!canDelete(maintenance.MaintenanceStatusTypeId)) {
            onWarning(text("NoPermission"));
            return;
        }

        setIsSaving(true)
        loader(true)
        try {
            const maintenanceToDelete = { ...maintenance, IsActive: false }
            await RoomPlannedMaintenance.UpdateRoomPlannedMaintenances([maintenanceToDelete]);
            if (onSave) onSave([maintenanceToDelete]);
            onSuccess();
            if (onClose) onClose();
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error);
        } finally {
            loader(false);
            setIsSaving(false);
        }
    }

    const closeAction = useCallback(() => {
        if (isDirty) {
            confirm({
                content: `${message("WouldYouLikeToContinue")}?`,
                confirmButton: { content: text('Yes'), positive: true },
                cancelButton: { content: text('No'), negative: true },
                onConfirm: () => {
                    if (onClose) onClose();
                }
            });
        } else {
            if (onClose) onClose();
        }
    }, [isDirty, confirm, message, text, onClose])

    // Field change handlers
    const setMaintenanceStatusType = val => {
        setMaintenance(prev => ({ ...prev, MaintenanceStatusTypeId: val }));
        setIsDirty(true);
    }

    const setDescription = val => {
        setMaintenance(prev => ({ ...prev, Description: val }));
        setIsDirty(true);
    }

    // Edit mode date handlers
    const setEditStartDate = val => {
        setMaintenance(prev => {
            const newState = { ...prev, StartDate: val ? moment(val).toDate() : null };
            if (newState.StartDate && newState.EndDate) {
                const minEnd = moment(newState.StartDate).add(1, 'days').startOf('day');
                if (moment(newState.EndDate).startOf('day').isBefore(minEnd)) {
                    newState.EndDate = null;
                }
            }
            return newState;
        });
        setIsDirty(true);
    }

    const setEditEndDate = val => {
        setMaintenance(prev => ({ ...prev, EndDate: val ? moment(val).toDate() : null }));
        setIsDirty(true);
    }

    // Create mode date handlers
    const handleStartDateChange = val => {
        handleStartEndChange([val, endDate], 0, setStartDate, setEndDate);
        setIsDirty(true);
    }

    const handleEndDateChange = val => {
        handleStartEndChange([startDate, val], 1, setStartDate, setEndDate);
        setIsDirty(true);
    }

    // Room handlers
    const handleRoomChange = val => {
        if (isEditMode) {
            setMaintenance(prev => ({ ...prev, RoomId: val }));
        } else {
            setSelectedRoomIds(Array.isArray(val) ? val : (val ? [val] : []));
        }
        setIsDirty(true);
    }

    const RoomsSpec = useMemo(() => {
        return maintenance?.HotelId ? { HotelId: maintenance.HotelId } : null;
    }, [maintenance?.HotelId]);

    // Determine which maintenance type options are disabled based on permissions
    // OOO = typeId 2, OOS = typeId 1
    const isOOODisabled = isEditMode
        ? (originalMaintenanceTypeId !== 2 && !canCreate(2))  // In edit: switching TO OOO requires CreateOOO (unless it's already OOO)
        : !canCreate(2);  // In create: need CreateOOO
    const isOOSDisabled = isEditMode
        ? (originalMaintenanceTypeId !== 1 && !canCreate(1))  // In edit: switching TO OOS requires CreateOOS (unless it's already OOS)
        : !canCreate(1);  // In create: need CreateOOS

    // In create mode, disable save if no weekdays are selected (user unchecked all days)
    const noWeekdaysSelected = !isEditMode && selectedDays.length === 0;

    const txtDelete = text("Delete");

    return (
        <ValidateModalPermission actionId={PermissionsActions.ViewRoomPlannedMaintenancePopup} open={rest.open} onClose={closeAction}>
            <Modal {...rest} size="small">
                <Modal.Header>{isEditMode ? text("EditMaintenance") : text("CreateMaintenance")}</Modal.Header>
                <Modal.Content>
                    <Form>
                        {/* Hotel Selection (mobile only) */}
                        {isMobileOnly && <Form.Field control={HotelsDropDown} spec={{ IsActive: true }} label={text("Hotel")} onChange={id => {
                            setMaintenance(prev => ({ ...prev, HotelId: id }));
                            setIsDirty(true);
                        }} value={maintenance.HotelId} />}

                        {/* === DATE RANGE SECTION === */}
                        {isEditMode ? (
                            /* Edit mode: single date range, no Add button */
                            <Form.Group widths={2}>
                                <Form.Field required>
                                    <label>{text('StartDate')}</label>
                                    <DatePicker
                                        onChange={setEditStartDate}
                                        minDate={moment(businessDate).startOf("day").toDate()}
                                        value={maintenance.StartDate || null}
                                        clearable
                                    />
                                </Form.Field>
                                <Form.Field required>
                                    <label>{text('EndDate')}</label>
                                    <DatePicker
                                        onChange={setEditEndDate}
                                        minRangeDays={1}
                                        startDate={maintenance.StartDate}
                                        value={maintenance.EndDate || null}
                                        clearable
                                    />
                                </Form.Field>
                            </Form.Group>
                        ) : (
                            /* Create mode: multiple date ranges with Add button */
                            <>
                                {/* Date range chips */}
                                {selectedDates.length > 0 && (
                                    <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                                        {selectedDates.map((dateRange, index) => (
                                            <Label.Group key={index}>
                                                <Label as="a">
                                                    {moment(dateRange.startDate).format(dateFormat)}
                                                    {' - '}
                                                    {moment(dateRange.endDate).format(dateFormat)}
                                                    <Icon name="delete" onClick={() => removeDateFromList(index)} />
                                                </Label>
                                            </Label.Group>
                                        ))}
                                    </Form.Group>
                                )}
                                <Form.Group widths="equal">
                                    <DatePicker
                                        key="fromDate"
                                        label={text("From")}
                                        onChange={handleStartDateChange}
                                        minDate={moment(businessDate).startOf("day").toDate()}
                                        clearable={false}
                                        value={startDate}
                                    />
                                    <DatePicker
                                        key="toDate"
                                        label={text("To")}
                                        onChange={handleEndDateChange}
                                        minDate={startDate || moment(businessDate).startOf("day").toDate()}
                                        minRangeDays={1}
                                        startDate={startDate}
                                        clearable={false}
                                        value={endDate}
                                    />
                                </Form.Group>
                                <Form.Group style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                                    <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={handleDaysChange} dates={memoDatesForWeekdays} />
                                    <div className="flex-center-center">
                                        <Button primary type="button" size="small" onClick={addDateToList}>{text("Add")}</Button>
                                    </div>
                                </Form.Group>
                            </>
                        )}


                        {/* === MAINTENANCE TYPE SECTION === */}
                        <Form.Field required>
                            <label>{text("ChooseAction")}</label>
                            <RadioSelectionInput
                                name="maintenanceType"
                                value={maintenance.MaintenanceStatusTypeId}
                                onChange={setMaintenanceStatusType}
                                options={[
                                    {
                                        value: 2,
                                        label: text("OutOfOrder"),
                                        description: message("OOODescription"),
                                        icon: 'ban',
                                        iconColor: 'red',
                                        disabled: isOOODisabled,
                                    },
                                    {
                                        value: 1,
                                        label: text("OutOfService"),
                                        description: message("OOSDescription"),
                                        icon: 'wrench',
                                        iconColor: 'grey',
                                        disabled: isOOSDisabled,
                                    },
                                ]}
                            />
                        </Form.Field>

                        {/* === ROOM SELECTION === */}
                        {isEditMode ? (
                            <Form.Field
                                label={text("Room")}
                                control={RoomsDropDown}
                                disabled
                                spec={RoomsSpec}
                                value={maintenance.RoomId}
                            />
                        ) : (
                            <Form.Field
                                label={text("Room")}
                                control={RoomsDropDown}
                                spec={RoomsSpec}
                                multiple
                                clearable
                                required
                                search
                                value={selectedRoomIds}
                                onChange={handleRoomChange}
                            />
                        )}

                        {/* === DESCRIPTION === */}
                        <Form.TextArea label={text("Description")} onChange={(e, { value }) => setDescription(value)} value={maintenance.Description} maxLength={4000} />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button disabled={isSaving} negative size="small" onClick={closeAction}>{text("Close")}</Button>
                    {isEditMode && !isVirtual && <Button disabled={isSaving} loading={isSaving} className="btn-delete" size="small" onClick={deleteAction}>{txtDelete}</Button>}
                    {!isEditMode && !isVirtual && <Button disabled={isSaving || noWeekdaysSelected} loading={isSaving} positive size="small" onClick={() => saveAction(true)}>{text("Saveandclose")}</Button>}
                    {!isVirtual && <Button disabled={isSaving || noWeekdaysSelected} loading={isSaving} positive size="small" onClick={() => saveAction(false)}>{text("Save")}</Button>}
                </Modal.Actions>
            </Modal>
        </ValidateModalPermission>
    )
}

RoomMaintenancePopup.propTypes = {
    onClose: PropTypes.func,
    maintenance: PropTypes.object,
    onSave: PropTypes.func
}

export default RoomMaintenancePopup
